import { defineStore } from 'pinia';
import hash from '@/shared/hash';
import * as userService from '@/services/user';
import type { QueryUserOverviewType, QueryProfileType } from '@/services/user';
import { omit, isUndefined } from 'lodash';
import SATracking from '@/shared/saTracking';
import { getBlackbox } from '@/shared/blackbox';
import { useUTMStore } from './utm';
import { useLoanStore } from './loan';
import { useBankcardStore } from './partner/bankcard';
import { useRecommendRankStore } from './recommendRank';
import { useRepaymentStore } from './repayment';
import { useWalletStore } from './wallet';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { setUser } from '@sentry/vue';
import { LoginSourceTypes } from '@/shared/constants';
import { clearHomeDataCache } from '@/shared/homeCache';

interface ClosedTime {
  regularCustomer: number | null;
}

interface MarkAsRead {
  salary: boolean;
  raiseCreditLimitTag: boolean;
}

export interface EverydayMarkAsRead {
  normalRepay: number | null;
  partnerRepay: number | null;

  normalLoanRecord: number | null;
  partnerLoanRecord: number | null;
}

export interface UserState {
  token: string | null;
  actToken: string | null;
  identifier: string | null;
  userId: number | null;
  isNewUser: boolean | null;
  overview: Partial<QueryUserOverviewType['Result']>;
  profile: Partial<QueryProfileType['Result']>;
  closedTime: ClosedTime;
  markAsRead: MarkAsRead;
  everydayMarkAsRead: EverydayMarkAsRead;
  isTelemarketing: boolean;
}

interface UpdatePayload {
  token?: string | null;
  actToken?: string | null;
  phone?: string | null;
  userId?: number | null;
  isNewUser?: boolean;
}

interface LoginPayload {
  phone: string;
  captcha: string;
  utmSource?: string;
  extra?: string;
  blackbox?: string;
  bindWechat?: boolean;
  wxAppId?: string;
  wxOpenId?: string;
  sourceType?: LoginSourceTypes;
  /**
   * 外部联系人userId
   * 用于企业微信新客领红包
   */
  externalUserId?: string;
}

interface LoginByPhoneVerifyPayload {
  phone: string;
  loginToken: string;
  utmSource?: string;
  sourceType?: LoginSourceTypes;
  /**
   * 外部联系人userId
   * 用于企业微信新客领红包
   */
  externalUserId?: string;
}

interface SyncLoginByAppPayload {
  phone?: string | null;
  token?: string | null;
  userId?: number | null;
}

interface SendCaptchaPayload {
  phone: string;
  bindWechat?: boolean;
  ticket?: string;
  randstr?: string;
}

interface FetchProfilePayload {
  shouldLogin?: boolean;
}

const WX_APP_ID = 'wx6e9f28803deedfe9';

const makeIdentifier = (v?: string | null) => (v == null ? null : hash(v));

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    token: null,
    actToken: null,
    identifier: null,
    isNewUser: null,
    userId: null,
    overview: {},
    profile: {},
    closedTime: {
      regularCustomer: null,
    },
    // 标记已读状态
    markAsRead: {
      salary: false, // 工资提额上线
      raiseCreditLimitTag: false, // 可提额标签
    },
    everydayMarkAsRead: {
      normalRepay: null, // 宜享花还款
      partnerRepay: null, // 合作方还款

      normalLoanRecord: null, // 宜享花借款记录
      partnerLoanRecord: null, // 合作方借款记录
    },
    isTelemarketing: false,
  }),
  getters: {
    isLogin(state) {
      return (
        (state.token != null && state.token !== '') ||
        (state.actToken != null && state.actToken !== '')
      );
    },
    isNotLogin() {
      return !this.isLogin;
    },
  },
  actions: {
    update(payload: UpdatePayload) {
      this.token = isUndefined(payload.token) ? this.token : payload.token;
      this.identifier = isUndefined(payload.phone)
        ? this.identifier
        : makeIdentifier(payload.phone);
      this.userId = isUndefined(payload.userId) ? this.userId : payload.userId;
      this.isNewUser = payload.isNewUser ?? this.isNewUser;
    },
    updateActToken(actToken: string) {
      this.actToken = actToken;
    },
    updateOverview(payload = {}) {
      this.overview = payload;
    },
    updateProfile(payload: Partial<QueryProfileType['Result']> = {}) {
      if (payload.userId) {
        this.userId = payload.userId;
      }

      this.profile = payload;
    },
    updateClosedTime(payload = {}) {
      this.closedTime = {
        ...this.closedTime,
        ...payload,
      };
    },
    saveMarkAsRead(payload: Partial<MarkAsRead>) {
      this.markAsRead = {
        ...this.markAsRead,
        ...payload,
      };
    },
    /**
     * 检查每日已读标记，若已读日期小于当日则清空为null表示未读
     */
    checkEverydayMarkAsRead() {
      const keys = Object.keys(this.everydayMarkAsRead) as (keyof EverydayMarkAsRead)[];
      const now = dayjs();

      keys.forEach((key) => {
        const prev = this.everydayMarkAsRead[key];

        if (prev != null && now.isAfter(prev, 'date')) {
          this.everydayMarkAsRead[key] = null;
        }
      });
    },
    saveEverydayMarkAsRead(key: keyof EverydayMarkAsRead) {
      this.everydayMarkAsRead[key] = dayjs().valueOf();
    },
    /**
     * 登录
     */
    async login(payload: LoginPayload) {
      const utmStore = useUTMStore();
      const utmSource = payload.utmSource ?? utmStore.source;
      let blackbox;

      try {
        blackbox = await getBlackbox();
      } catch {}

      try {
        const res = await userService.login({
          phone: payload.phone,
          verifyCode: payload.captcha,
          utmSource,
          blackBox: blackbox ?? '',
          wxAppId: WX_APP_ID,
          wxOpenId: Cookies.get('openid'),
          utmParam: window.location.search.slice(1), // 所有参数透传给登录接口
          origin: payload.sourceType,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          externalUserId: payload.externalUserId,
        });
        const isNewUser = Number(res.isRegister) === 1;
        const identifier = makeIdentifier(payload.phone);

        if (this.identifier !== identifier) {
          await this.logout();
        }

        this.update({
          token: res.token,
          phone: payload.phone,
          userId: res.userId,
          isNewUser,
        });

        this.associateUserWithThirdParty();

        // 神策统计区分新老用户
        SATracking.trackLogin({
          is_new: isNewUser,
          utm_source: utmSource,
        });

        return res;
      } catch (err) {
        // 神策统计注册失败及原因
        SATracking.trackInterfaceResults({
          desc: '申请注册结果',
          describe: SATracking.serialize({
            status_name: '失败',
            msg: (err as Error).message,
          }),
        });
        throw err;
      }
    },
    /**
     * 极光号码认证登录（透传参数）
     */
    async loginByPhoneVerify(payload: LoginByPhoneVerifyPayload) {
      const utmStore = useUTMStore();
      const utmSource = payload.utmSource ?? utmStore.source;
      let blackbox;

      try {
        blackbox = await getBlackbox();
      } catch {}

      try {
        const res = await userService.loginByPhoneVerify({
          phone: payload.phone,
          loginToken: payload.loginToken,
          utmSource,
          blackBox: blackbox ?? '',
          wxAppId: WX_APP_ID,
          wxOpenId: Cookies.get('openid'),
          utmParam: window.location.search.slice(1),
          origin: payload.sourceType,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          externalUserId: payload.externalUserId,
        });

        if (!res.passed) {
          return res;
        }

        const identifier = makeIdentifier(payload.phone);

        if (this.identifier !== identifier) {
          await this.logout();
        }

        const ext = res as { userId?: number; isRegister?: number | string };
        const nextUser: UpdatePayload = {
          token: res.token,
          phone: payload.phone,
          userId: ext.userId,
        };
        if (ext.isRegister != null) {
          nextUser.isNewUser = Number(ext.isRegister) === 1;
        }
        this.update(nextUser);

        this.associateUserWithThirdParty();

        SATracking.trackLogin({
          is_new: this.isNewUser ?? false,
          utm_source: utmSource,
        });

        return res;
      } catch (err) {
        SATracking.trackInterfaceResults({
          desc: '申请注册结果',
          describe: SATracking.serialize({
            status_name: '失败',
            msg: (err as Error).message,
          }),
        });
        throw err;
      }
    },
    /**
     * 通过APP同步登录
     */
    syncLoginByApp(payload: SyncLoginByAppPayload) {
      const identifier = makeIdentifier(payload.phone);

      if (this.identifier !== identifier) {
        this.logout();
      }

      this.update({
        token: payload.token,
        phone: payload.phone,
        userId: payload.userId,
      });

      this.associateUserWithThirdParty();
    },
    /**
     * 关联用户信息至三方系统
     */
    associateUserWithThirdParty() {
      // 同步用户ID关联神策
      if (this.userId != null) {
        SATracking.login(this.userId);
      }

      // 同步用户ID/Token关联Sentry
      if (
        this.userId != null &&
        this.token != null &&
        (process.env.VUE_APP_RUNTIME_ENV === 'prod' ||
          process.env.VUE_APP_RUNTIME_ENV === 'staging')
      ) {
        setUser({
          userId: this.userId,
          token: this.token,
        });
      }
    },
    /**
     * 退出
     */
    async logout() {
      const loanStore = useLoanStore();
      const recommendRankStore = useRecommendRankStore();
      const repaymentStore = useRepaymentStore();
      const walletStore = useWalletStore();
      const bankcardStore = useBankcardStore();

      loanStore.$reset();
      recommendRankStore.$reset();
      repaymentStore.$reset();
      walletStore.$reset();
      bankcardStore.$reset();
      clearHomeDataCache();

      this.$reset();

      // 退出神策登录状态
      SATracking.logout();

      // 清除Sentry关联的用户ID及Token
      if (
        process.env.VUE_APP_RUNTIME_ENV === 'prod' ||
        process.env.VUE_APP_RUNTIME_ENV === 'staging'
      ) {
        setUser({
          userId: null,
          token: null,
        });
      }
    },
    /**
     * 发送登录短信验证码
     */
    async sendCaptcha({ phone, ticket, randstr, bindWechat }: SendCaptchaPayload) {
      const utmStore = useUTMStore();
      await userService.sendCaptcha({
        phone,
        ticket,
        randstr,
        bindWechat,
        wxAppId: WX_APP_ID,
        wxOpenId: Cookies.get('openid'),
        utmSource: utmStore.source,
        utmParam: window.location.search.slice(1), // 所有参数透传给登录接口
      });
    },
    /**
     * 发送注销短信验证码
     */
    async sendCloseAccountCaptcha({ phone, ticket, randstr }: SendCaptchaPayload) {
      const utmStore = useUTMStore();
      await userService.sendCloseAccountCaptcha({
        phone,
        ticket,
        randstr,
        utmSource: utmStore.source,
      });
    },
    /**
     * 获取用户概览
     */
    async fetchOverview() {
      const overview = await userService.queryUserOverview();
      this.updateOverview(overview);
    },
    /**
     * 获取用户信息
     */
    async fetchProfile({ shouldLogin }: FetchProfilePayload = {}) {
      const profile = await userService.queryProfile({ shouldLogin });

      const { utmSource, utmSourceName } = profile ?? {};
      this.updateProfile(omit(profile, ['utmSource', 'utmSourceName']));

      const utmStore = useUTMStore();
      utmStore.update({
        source: utmSource,
        name: utmSourceName,
      });

      return profile;
    },
    /**
     * 标记为电销客户
     */
    markTelemarketing() {
      this.isTelemarketing = true;
    },
    /**
     * 取消标记为电销客户
     */
    unmarkTelemarketing() {
      this.isTelemarketing = false;
    },
    /**
     * 检查是否已绑定微信
     */
    async checkWeChatBinding() {
      const res = await userService.checkWeChatBinding({
        wxAppId: WX_APP_ID,
        wxOpenId: Cookies.get('openid'),
      });

      return res;
    },
  },
  persist: {
    paths: [
      'token',
      'identifier',
      'userId',
      'isNewUser',
      'closedTime',
      'markAsRead',
      'everydayMarkAsRead',
      'isTelemarketing',
    ],
  },
});
